'use client'

import React from 'react'
import { Header, HeaderProps } from './header'
import { Footer, FooterProps } from './footer'
import { useTheme } from '../theme/provider'

export interface PageLayoutProps {
  /**
   * Header configuration
   */
  header: HeaderProps
  /**
   * Footer configuration
   */
  footer: FooterProps
  /**
   * Page content
   */
  children: React.ReactNode
  /**
   * Additional class names for main content
   */
  contentClassName?: string
}

/**
 * PageLayout Component - Standard page structure
 *
 * Provides consistent layout structure for all pages:
 * - Fixed header at top with automatic spacing compensation
 * - Main content area with proper padding
 * - Footer at bottom
 * - Ensures all pages have the same spacing standards
 *
 * Benefits:
 * - Automatic header height compensation
 * - Consistent spacing across all pages
 * - Flex layout ensures footer stays at bottom
 * - Single source of truth for page structure
 *
 * @example
 * ```tsx
 * <PageLayout
 *   header={{
 *     logoUrl: "/logo.svg",
 *     siteTitle: "My Site",
 *     locale: "en",
 *     navItems: [...],
 *     languages: [...],
 *   }}
 *   footer={{
 *     logoUrl: "/logo.svg",
 *     siteTitle: "My Site",
 *     locale: "en",
 *     sections: [...],
 *   }}
 * >
 *   <YourPageContent />
 * </PageLayout>
 * ```
 */
export function PageLayout({
  header,
  footer,
  children,
  contentClassName = '',
}: PageLayoutProps) {
  const { theme } = useTheme()

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: theme.colors.background }}>
      {/* Fixed Header */}
      <Header {...header} />

      {/* Main Content - automatically compensates for fixed header */}
      <main
        className={`flex-1 pt-16 md:pt-20 ${contentClassName}`}
        style={{ backgroundColor: theme.colors.background }}
      >
        {children}
      </main>

      {/* Footer */}
      <Footer {...footer} />
    </div>
  )
}
